import { GetLicenseInformationResponse } from '@atlassiansox/asc-stargate';

import { ufoEventOnboardingInviteUsers } from '@/components/signup/utils/ufo-events';
import { UfoCustomTimingKey } from '@/components/signup/utils/ufo.types';

import { extractAtlOriginFromUrl } from '../../../utils/extract-alt-url';
import { generateContinueUrl } from '../utils';

import {
	Fetched,
	FetchParams,
	InternalJiraEndpoints,
	JIRA_PRODUCT_KEY,
	TENANT_AWARE_REDIRECT,
} from './types';
import { ProjectRoleIds } from './use-invite-step-fetch-project-role-ids';

export const getInviteeProjectRoleId = (
	projectRoleIds: Fetched<ProjectRoleIds>,
	preferAdminRole = false,
) => {
	if (!projectRoleIds) {
		return null;
	}

	if (preferAdminRole) {
		return projectRoleIds.admin || projectRoleIds.member || projectRoleIds.viewer || null;
	}

	// fallback order to ensure at least one role is assigned
	return projectRoleIds.member || projectRoleIds.admin || projectRoleIds.viewer || null;
};

const formatPrefix = (cloudId: string) => TENANT_AWARE_REDIRECT.replace('$cloudId', cloudId);

type InterceptorRequestHandler = (
	fetchRef: typeof fetch,
	params: FetchParams,
	args: {
		cloudId: string;
		projectId?: string | null;
		inviteeRoleId?: number | null;
		onComplete?: (args: object) => Promise<void>;
		onError?: (args: object) => Promise<void>;
	},
) => Promise<Response>;

export const permissionHandler: InterceptorRequestHandler = async (fetchRef, params, args) => {
	const [, config] = params;
	const { cloudId } = args;
	const tenantAwarePath = formatPrefix(cloudId);

	const modifiedResource = `${tenantAwarePath}${InternalJiraEndpoints.JIRA_PERMISSIONS}?permissions=USER_PICKER`;

	return fetchRef(modifiedResource, config);
};

export const jiraInviteHandler: InterceptorRequestHandler = async (fetchRef, params, args) => {
	ufoEventOnboardingInviteUsers.startSubstep(
		UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_INVITING_USERS,
	);

	const [, config] = params;
	const { cloudId, projectId, inviteeRoleId } = args;
	const tenantAwarePath = formatPrefix(cloudId);

	if (!projectId || !config?.body || !inviteeRoleId) {
		return fetchRef(...params);
	}

	const modifiedResource = `${tenantAwarePath}${InternalJiraEndpoints.JIRA_INVITE_USER}/${projectId}`;

	const body = JSON.parse(config.body as string);

	const modifiedBody = {
		...body,
		projectRoleIds: inviteeRoleId ? [inviteeRoleId] : [],
		continueUrl: generateContinueUrl(projectId),
		atlOrigin: extractAtlOriginFromUrl(body.continueUrl || ''),
	};

	const modifiedConfig = {
		...config,
		body: JSON.stringify(modifiedBody),
	};

	return fetchRef(modifiedResource, modifiedConfig);
};

export const bulkInviteHandler: InterceptorRequestHandler = async (fetchRef, params, args) => {
	ufoEventOnboardingInviteUsers.startSubstep(
		UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_INVITING_USERS,
	);

	const [, config] = params;
	const { cloudId, projectId, inviteeRoleId, onComplete, onError } = args;
	const tenantAwarePath = formatPrefix(cloudId);

	if (!projectId || !config?.body || !inviteeRoleId) {
		return fetchRef(...params);
	}

	// redirect Bulk Invite call to Jira internal Invite endpoint
	const modifiedResource = `${tenantAwarePath}${InternalJiraEndpoints.JIRA_INVITE_USER}/${projectId}`;

	const body = JSON.parse(config.body as string);

	const emails: Array<{ email: string }> = body.users || [];

	if (!emails.length || emails.length > 10) {
		// 10 is Invite People's maximum allowed invites per request
		return fetchRef(...params);
	}

	const continueUrl = generateContinueUrl(projectId);
	const atlOrigin = extractAtlOriginFromUrl(body.continueUrl || '');

	// for each email added, create a separate invite request and invite all in parallel
	const invites = emails.map(({ email }) => {
		const inviteBody = {
			email,
			projectRoleIds: inviteeRoleId ? [inviteeRoleId] : [],
			continueUrl,
			atlOrigin,
		};

		const inviteCofig = {
			...config,
			body: JSON.stringify(inviteBody),
		};

		return fetchRef(modifiedResource, inviteCofig);
	});

	const results = await Promise.allSettled(invites);

	// deterime how many invites succeeded for analytics
	const successfulInvites = results.filter(
		(result) => result.status === 'fulfilled' && result.value.ok,
	);
	const failedInvites = results.filter(
		(result) => result.status === 'rejected' || !result.value.ok,
	);

	await onComplete?.({
		totalInvites: results.length,
		successfulInvites: successfulInvites.length,
		failedInvites: failedInvites.length,
	});

	if (onError) {
		results.forEach((result) => formatAndFireOnError(result, onError));
	}

	// Return first result to satisfy the expected return type of Response, or a fallback
	if (results[0]?.status === 'fulfilled') {
		return results[0].value;
	}

	// as a default, make original call
	return fetchRef(...params);
};

export const requestHandlers: Record<InternalJiraEndpoints, InterceptorRequestHandler> = {
	[InternalJiraEndpoints.JIRA_PERMISSIONS]: permissionHandler,
	[InternalJiraEndpoints.JIRA_INVITE_USER]: jiraInviteHandler,
	[InternalJiraEndpoints.JIRA_INVITE_USER_FALLBACK]: bulkInviteHandler,
};

const formatAndFireOnError = (
	result: PromiseSettledResult<Response>,
	onError: (args: object) => Promise<void>,
) => {
	if (result.status === 'fulfilled' && !result.value.ok) {
		const clone = result.value.clone(); // cloning to preserve original responses after interception
		clone
			.json()
			.then((asJson) => {
				onError({
					error: asJson || 'Unknown error',
					status: clone.status,
					statusText: clone.statusText,
				});
			})
			.catch();
	} else if (result.status === 'rejected') {
		onError({
			error: result.reason || 'Unknown error: promise rejected',
		});
	}
};

export const parseJiraEditionFromLicenseInformation = (response: GetLicenseInformationResponse) => {
	return response.products[JIRA_PRODUCT_KEY]?.edition ?? 'premium';
};
